// src/core/score/mixedDealScore.ts
// Bruecke zwischen den Mixed-Use-Mechanik-Kennzahlen (Zwei-Segmente-Modell
// Wohnen/Gewerbe aus MixedUseCheck.tsx, Funktion `calcMixed`) und dem
// analyzer-uebergreifenden Deal-Score (dealScore.ts). Gleiches Muster wie
// etwDealScore.ts/mfhDealScore.ts. Finanzierung bewusst flach (loan =
// kaufpreis*ltv, annu = loan*(zins+tilgung)), genau wie im bestehenden
// `calcMixed()` -- kein annuityExact wie bei EFH/Gewerbe.

import { computeDealScore, type DealScoreResult, type LageInput } from "./dealScore.js";
import { GEMISCHT_SCORE_CONFIG, RENDITE_ZUSCHLAG_PP, BEWIRTSCHAFTUNG_PAUSCHALE_PCT } from "./dealScoreConfig.js";
import { resolveImportPcts, pctSum } from "../../lib/purchaseCosts.js";
import { computeFinancing, eigenkapitalFromLegacyLoan } from "../financing.js";
import type { MixedSnapshotInput } from "../../lib/mixedSnapshot.js";

const STRESS_ZINS_DELTA = 0.01;
const STRESS_MIETE_FAKTOR = 0.9;

export type MixedMetricsInput = {
  kaufpreis: number;
  priceAdjPct: number;
  applyAdjustments: boolean;
  nkPct: number;
  financingOn: boolean;
  /** Vereinheitlichte Darlehensregel (16.10.2026), wie MFH/EFH/Gewerbe: Eigenkapital in
   *  Euro statt Beleihungsquote (LTV). */
  eigenkapital: number;
  zinsPct: number;
  tilgungPct: number;
  wFl: number;
  wRentM2: number;
  wLeer: number;
  wOpexBrutto: number;
  wRentAdjPct: number;
  /** Phase-4 Runde 2b, Baustein 4 (Bankbericht aus der Akte): Cap-Rate je Segment, nur
   *  fuer die additiven wertW/wertG/wertAusCap-Felder unten -- fliesst NICHT in
   *  kaufpreisView/allIn/noiJahr/Score ein. */
  wCap: number;
  gFl: number;
  gRentM2: number;
  gLeer: number;
  gOpexBrutto: number;
  gRentAdjPct: number;
  gCap: number;
  // Bewirtschaftung Pauschal/Einzeln (Baustein 5, Korrektur der frueheren "3 % uebernehmen"-
  // Vorgabe): EIN Umschalter fuer das ganze Objekt (wie bei den anderen 4 Typen), wirkt aber
  // auf BEIDE Segmente. Legacy-Zweig (kein Modus): wOpexBrutto/gOpexBrutto unveraendert, wie
  // bisher. Pauschal: je Segment effW/effG x BEWIRTSCHAFTUNG_PAUSCHALE_PCT.mixeduse (22 %).
  // Einzeln: je Segment nicht umlagefaehig + Verwaltung + Instandhaltung (€/m² x wFl/gFl) --
  // NUR fuer den Wohnteil gibt es einen § 28 II. BV-Vorschlag (gilt nicht fuer Gewerbe).
  bewirtschaftungModus?: "pauschal" | "einzeln";
  wNichtUmlagefaehig?: number;
  wVerwaltung?: number;
  wInstandhaltungJeM2Jahr?: number;
  gNichtUmlagefaehig?: number;
  gVerwaltung?: number;
  gInstandhaltungJeM2Jahr?: number;
  // Stress-Pass-Fix (18.10.2026, Dominiks Vorgabe): siehe MfhMetricsInput.pauschaleBasisMiete
  // (core/metrics/mfh.ts) -- identisches Muster, hier je Segment (Wohnen/Gewerbe).
  pauschaleBasisEffW?: number;
  pauschaleBasisEffG?: number;
};

export type MixedMetrics = {
  kaufpreisView: number;
  allIn: number;
  grossRentYear: number;
  /** Erzielte (Ist-)Jahresmiete nach Leerstand (wLeer/gLeer) -- fuer Anzeigen, die die
   *  tatsaechliche statt der vollvermieteten Miete zeigen sollen (Nachtest-Korrektur
   *  12.10.2026, Punkt 1), z. B. die Bruttorendite-Kachel in /check. */
  effRentYear: number;
  noiJahr: number;
  loan: number;
  /** Bedarf (All-in) minus EK minus Kredit; 0, wenn das Eigenkapital die Nebenkosten deckt. */
  finanzierungsluecke: number;
  annuitaetJahr: number;
  /** Phase-4 Runde 2b, Baustein 4 (Bankbericht aus der Akte): Wohnen/Gewerbe-Aufschluesselung
   *  sowie der Cap-Rate-Modellwert je Segment -- additiv, bisher nur lokal in
   *  MixedUseCheck.tsx (`calcMixed()`) berechnet, aendert kaufpreisView/allIn/noiJahr/
   *  Score nicht. */
  grossW: number;
  effW: number;
  opexW: number;
  /** Nur im Einzeln-Modus > 0 (Pauschal/Legacy: 0) -- fuer die Breakdown-Anzeige. */
  wVerwaltungEff: number;
  noiW: number;
  grossG: number;
  effG: number;
  opexG: number;
  gVerwaltungEff: number;
  noiG: number;
  wertW: number;
  wertG: number;
  wertAusCap: number;
};

export function computeMixedMetrics(input: MixedMetricsInput): MixedMetrics {
  const adjustedPrice = Math.round(input.kaufpreis * (1 + input.priceAdjPct));
  const kaufpreisView = input.applyAdjustments ? adjustedPrice : input.kaufpreis;

  const wRentM2Adj = input.applyAdjustments ? input.wRentM2 * (1 + input.wRentAdjPct) : input.wRentM2;
  const gRentM2Adj = input.applyAdjustments ? input.gRentM2 * (1 + input.gRentAdjPct) : input.gRentM2;

  const grossW = input.wFl * wRentM2Adj * 12;
  const effW = grossW * (1 - input.wLeer);
  const grossG = input.gFl * gRentM2Adj * 12;
  const effG = grossG * (1 - input.gLeer);

  // Bewirtschaftung Pauschal/Einzeln (Baustein 5) -- siehe Feldkommentar in MixedMetricsInput.
  let opexW: number, wVerwaltungEff: number, opexG: number, gVerwaltungEff: number;
  if (input.bewirtschaftungModus === "pauschal") {
    opexW = (input.pauschaleBasisEffW ?? effW) * BEWIRTSCHAFTUNG_PAUSCHALE_PCT.mixeduse;
    wVerwaltungEff = 0;
    opexG = (input.pauschaleBasisEffG ?? effG) * BEWIRTSCHAFTUNG_PAUSCHALE_PCT.mixeduse;
    gVerwaltungEff = 0;
  } else if (input.bewirtschaftungModus === "einzeln") {
    const wInstandhaltung = Math.max(0, input.wInstandhaltungJeM2Jahr ?? 0) * Math.max(0, input.wFl);
    wVerwaltungEff = Math.max(0, input.wVerwaltung ?? 0);
    opexW = Math.max(0, input.wNichtUmlagefaehig ?? 0) + wVerwaltungEff + wInstandhaltung;
    const gInstandhaltung = Math.max(0, input.gInstandhaltungJeM2Jahr ?? 0) * Math.max(0, input.gFl);
    gVerwaltungEff = Math.max(0, input.gVerwaltung ?? 0);
    opexG = Math.max(0, input.gNichtUmlagefaehig ?? 0) + gVerwaltungEff + gInstandhaltung;
  } else {
    // Legacy (kein Modus, z.B. unveraenderter freier Einstieg): exakt wie bisher.
    opexW = grossW * input.wOpexBrutto;
    wVerwaltungEff = 0;
    opexG = grossG * input.gOpexBrutto;
    gVerwaltungEff = 0;
  }
  const noiW = effW - opexW;
  const noiG = effG - opexG;

  const noiJahr = noiW + noiG;
  const grossRentYear = grossW + grossG;
  const effRentYear = effW + effG;

  const nkBetrag = Math.round(kaufpreisView * input.nkPct);
  const allIn = kaufpreisView + nkBetrag;

  // Finanzierung (Dominiks Vorgabe, 16.10.2026, einheitliche Darlehensregel -- wie
  // MFH/EFH/Gewerbe): gemeinsame Funktion (core/financing.ts) statt Kaufpreis x LTV.
  const financing = computeFinancing({ kaufpreis: kaufpreisView, nebenkosten: nkBetrag, eigenkapital: input.eigenkapital, financingOn: input.financingOn });
  const loan = financing.loan;
  const finanzierungsluecke = financing.finanzierungsluecke;
  const annuitaetJahr = input.financingOn ? loan * (input.zinsPct + input.tilgungPct) : 0;

  const wertW = input.wCap > 0 ? noiW / input.wCap : 0;
  const wertG = input.gCap > 0 ? noiG / input.gCap : 0;
  const wertAusCap = wertW + wertG;

  return {
    kaufpreisView, allIn, grossRentYear, effRentYear, noiJahr, loan, finanzierungsluecke, annuitaetJahr,
    grossW, effW, opexW, wVerwaltungEff, noiW, grossG, effG, opexG, gVerwaltungEff, noiG, wertW, wertG, wertAusCap,
  };
}

/** Migration (16.10.2026, einheitliche Darlehensregel): ein alter Gemischt-Snapshot/-Export
 *  (vor der Umstellung von ltvPct auf eigenkapital) speicherte Kredit = Kaufpreis x ltvPct,
 *  ohne NK-Deckel. Aequivalentes Eigenkapital ueber die gemeinsame Migrations-Funktion
 *  (core/financing.ts). */
export function mixedEigenkapitalFromLegacyLtv(kaufpreis: number, ltvPct: number, nkPct: number): number {
  const nkBetrag = Math.round(kaufpreis * nkPct);
  const kreditAlt = kaufpreis * ltvPct;
  return eigenkapitalFromLegacyLoan(kaufpreis + nkBetrag, kreditAlt);
}

/** Loest das Eigenkapital aus einem (ggf. alten) Gemischt-Snapshot-Input auf -- wendet
 *  mixedEigenkapitalFromLegacyLtv() an, wenn legacyLtvPct gesetzt ist, sonst der direkte
 *  Wert. */
export function resolveMixedEigenkapital(
  rawInput: { kaufpreis: number; eigenkapital: number; legacyLtvPct: number | null },
  nkPct: number
): number {
  return rawInput.legacyLtvPct == null
    ? rawInput.eigenkapital
    : mixedEigenkapitalFromLegacyLtv(rawInput.kaufpreis, rawInput.legacyLtvPct, nkPct);
}

/** Historische nicht-umlagefaehige Betraege (€/Jahr) aus wOpexBrutto/gOpexBrutto -- fuer die
 *  Migrationsumrechnung unten (Gemischt hat kein eigenes Instandhaltungsfeld, nur diese
 *  kombinierten Prozentsaetze). */
export function mixedLegacyBewirtschaftung(
  wFl: number, wRentM2: number, wOpexBrutto: number,
  gFl: number, gRentM2: number, gOpexBrutto: number
): { wNichtUmlagefaehig: number; gNichtUmlagefaehig: number } {
  return { wNichtUmlagefaehig: wFl * wRentM2 * 12 * wOpexBrutto, gNichtUmlagefaehig: gFl * gRentM2 * 12 * gOpexBrutto };
}

/**
 * Migrations-Bruecke Bewirtschaftung Pauschal/Einzeln (Baustein 5) -- ANDERS als
 * resolveMfhBewirtschaftung()/resolveEfhBewirtschaftung()/resolveGewerbeBewirtschaftung():
 * Gemischt hatte VOR diesem Feature kein eigenes Instandhaltungsfeld (nur das kombinierte
 * wOpexBrutto/gOpexBrutto), es gibt also keinen "war Instandhaltung je 0" Anhaltspunkt fuer
 * resolveBewirtschaftungModus()' Heuristik. Ein alter Snapshot ohne gespeicherten Modus wird
 * deshalb IMMER "einzeln" (nicht "pauschal"), mit wOpexBrutto/gOpexBrutto 1:1 als
 * nichtUmlagefaehig uebernommen -- das reproduziert den historischen Score exakt
 * (Bestandsschutz). Neue, ueber das "Annahmen pruefen"-Formular angelegte Objekte setzen den
 * Modus explizit (UI-Default "pauschal").
 */
export function resolveMixedBewirtschaftung(
  rawInput: {
    bewirtschaftungModus?: unknown;
    wNichtUmlagefaehig?: number; wVerwaltung?: number; wInstandhaltungJeM2Jahr?: number;
    gNichtUmlagefaehig?: number; gVerwaltung?: number; gInstandhaltungJeM2Jahr?: number;
  },
  legacyWNichtUmlagefaehig: number,
  legacyGNichtUmlagefaehig: number
): {
  bewirtschaftungModus: "pauschal" | "einzeln";
  wNichtUmlagefaehig: number; wVerwaltung: number; wInstandhaltungJeM2Jahr: number;
  gNichtUmlagefaehig: number; gVerwaltung: number; gInstandhaltungJeM2Jahr: number;
} {
  if (rawInput.bewirtschaftungModus === "pauschal") {
    return { bewirtschaftungModus: "pauschal", wNichtUmlagefaehig: 0, wVerwaltung: 0, wInstandhaltungJeM2Jahr: 0, gNichtUmlagefaehig: 0, gVerwaltung: 0, gInstandhaltungJeM2Jahr: 0 };
  }
  if (rawInput.bewirtschaftungModus === "einzeln") {
    return {
      bewirtschaftungModus: "einzeln",
      wNichtUmlagefaehig: rawInput.wNichtUmlagefaehig ?? 0, wVerwaltung: rawInput.wVerwaltung ?? 0, wInstandhaltungJeM2Jahr: rawInput.wInstandhaltungJeM2Jahr ?? 0,
      gNichtUmlagefaehig: rawInput.gNichtUmlagefaehig ?? 0, gVerwaltung: rawInput.gVerwaltung ?? 0, gInstandhaltungJeM2Jahr: rawInput.gInstandhaltungJeM2Jahr ?? 0,
    };
  }
  return {
    bewirtschaftungModus: "einzeln",
    wNichtUmlagefaehig: legacyWNichtUmlagefaehig, wVerwaltung: 0, wInstandhaltungJeM2Jahr: 0,
    gNichtUmlagefaehig: legacyGNichtUmlagefaehig, gVerwaltung: 0, gInstandhaltungJeM2Jahr: 0,
  };
}

function toDealScoreInput(
  metrics: MixedMetrics,
  stressMetrics: MixedMetrics,
  financingOn: boolean,
  lage: LageInput,
  vermietungsstatus: "vollstaendig" | "teilweise" | "nein"
) {
  return {
    kaufpreis: metrics.kaufpreisView,
    gesamtinvestition: metrics.allIn,
    jahreskaltmiete: metrics.grossRentYear,
    noiJahr: metrics.noiJahr,
    annuitaetJahr: metrics.annuitaetJahr,
    financingOn,
    stressNoiJahr: stressMetrics.noiJahr,
    stressAnnuitaetJahr: stressMetrics.annuitaetJahr,
    lage,
    renditeZuschlagPp: RENDITE_ZUSCHLAG_PP.mixeduse,
    vermietungsstatus,
  };
}

/** Fuer die lebende Analyzer-Seite: metrics ist das Ergebnis eines bereits erfolgten computeMixedMetrics()-Aufrufs. */
export function computeMixedDealScoreFromMetrics(
  baseInput: MixedMetricsInput,
  metrics: MixedMetrics,
  lage: LageInput,
  vermietungsstatus: "vollstaendig" | "teilweise" | "nein" = "vollstaendig"
): DealScoreResult {
  const stressInput: MixedMetricsInput = {
    ...baseInput,
    zinsPct: baseInput.zinsPct + STRESS_ZINS_DELTA,
    wRentM2: baseInput.wRentM2 * STRESS_MIETE_FAKTOR,
    gRentM2: baseInput.gRentM2 * STRESS_MIETE_FAKTOR,
    // Stress-Pass-Fix (18.10.2026): Pauschal je Segment auf Basis-Miete fix, siehe mfhDealScore.ts.
    pauschaleBasisEffW: metrics.effW,
    pauschaleBasisEffG: metrics.effG,
  };
  const stressMetrics = computeMixedMetrics(stressInput);
  return computeDealScore(
    toDealScoreInput(metrics, stressMetrics, baseInput.financingOn, lage, vermietungsstatus),
    GEMISCHT_SCORE_CONFIG
  );
}

/** Fuer "Meine Deals"/PDF: nur die gespeicherten Rohdaten vorhanden, kein live computeMixedMetrics()-Ergebnis. */
export function computeMixedDealScoreFromSnapshotInput(
  rawInput: MixedSnapshotInput & Record<string, unknown>,
  playground: { priceAdjPct: number; wRentAdjPct: number; gRentAdjPct: number; applyAdjustments: boolean },
  lage: LageInput,
  vermietungsstatus: "vollstaendig" | "teilweise" | "nein" = "vollstaendig"
): DealScoreResult {
  const nkPct = pctSum(resolveImportPcts(rawInput, "mixeduse"));
  const legacy = mixedLegacyBewirtschaftung(rawInput.wFl, rawInput.wRentM2, rawInput.wOpexBrutto, rawInput.gFl, rawInput.gRentM2, rawInput.gOpexBrutto);
  const bewirtschaftung = resolveMixedBewirtschaftung(rawInput, legacy.wNichtUmlagefaehig, legacy.gNichtUmlagefaehig);
  const base: MixedMetricsInput = {
    kaufpreis: rawInput.kaufpreis,
    priceAdjPct: playground.priceAdjPct,
    applyAdjustments: playground.applyAdjustments,
    nkPct,
    financingOn: rawInput.financingOn,
    eigenkapital: resolveMixedEigenkapital(rawInput as any, nkPct),
    zinsPct: rawInput.zinsPct,
    tilgungPct: rawInput.tilgungPct,
    wFl: rawInput.wFl,
    wRentM2: rawInput.wRentM2,
    wLeer: rawInput.wLeer,
    wOpexBrutto: rawInput.wOpexBrutto,
    wRentAdjPct: playground.wRentAdjPct,
    wCap: rawInput.wCap,
    gFl: rawInput.gFl,
    gRentM2: rawInput.gRentM2,
    gLeer: rawInput.gLeer,
    gOpexBrutto: rawInput.gOpexBrutto,
    gRentAdjPct: playground.gRentAdjPct,
    gCap: rawInput.gCap,
    bewirtschaftungModus: bewirtschaftung.bewirtschaftungModus,
    wNichtUmlagefaehig: bewirtschaftung.wNichtUmlagefaehig, wVerwaltung: bewirtschaftung.wVerwaltung, wInstandhaltungJeM2Jahr: bewirtschaftung.wInstandhaltungJeM2Jahr,
    gNichtUmlagefaehig: bewirtschaftung.gNichtUmlagefaehig, gVerwaltung: bewirtschaftung.gVerwaltung, gInstandhaltungJeM2Jahr: bewirtschaftung.gInstandhaltungJeM2Jahr,
  };
  const metrics = computeMixedMetrics(base);
  return computeMixedDealScoreFromMetrics(base, metrics, lage, vermietungsstatus);
}

/** "Break-even-Kaufpreis" fuer Gemischt (Dominiks Vorgabe, Baustein 4, Bankbericht aus der
 *  Akte): aus MixedUseCheck.tsx extrahiert (dort `breakEvenPriceForCashflowZeroMixed`),
 *  UNVERAENDERT uebernommen -- Feld-fuer-Feld-Paritaet zum heutigen PDF erfordert exakt
 *  dieselbe Bisektion, nicht den generischen Akte-Solver (dealScoreBreakeven.ts), der mit
 *  anderer Iterationstiefe/Klammerung leicht abweichende Werte liefern wuerde. */
export function breakEvenPriceForCashflowZeroMixed(base: {
  kaufpreis: number;
  financingOn: boolean;
  eigenkapital: number;
  nkPct: number;
  zinsPct: number;
  tilgungPct: number;
  grossW: number;
  effW: number;
  opexW: number;
  grossG: number;
  effG: number;
  opexG: number;
}): number | null {
  if (!base.financingOn || base.zinsPct + base.tilgungPct <= 0) return null;
  const eff = base.effW + base.effG;
  const opex = base.opexW + base.opexG;
  // Nebenkosten skalieren mit dem (hier variierenden) Kaufpreis -- je Iteration ueber
  // computeFinancing() neu bestimmt, statt eines fixen Beleihungs-Multiplikators
  // (Dominiks Vorgabe, 16.10.2026, einheitliche Darlehensregel).
  const cfAt = (price: number) => {
    const nebenkosten = Math.round(price * base.nkPct);
    const loan = computeFinancing({ kaufpreis: price, nebenkosten, eigenkapital: base.eigenkapital, financingOn: true }).loan;
    const annu = loan * (base.zinsPct + base.tilgungPct);
    return (eff - opex - annu) / 12;
  };
  let low = 0, high = Math.max(1, base.kaufpreis), cfH = cfAt(high), guard = 0;
  while (cfH > 0 && high < base.kaufpreis * 100 && guard < 50) {
    high *= 1.5;
    cfH = cfAt(high);
    guard++;
  }
  if (cfH > 0) return Math.round(high);
  for (let i = 0; i < 50; i++) {
    const mid = (low + high) / 2;
    const cf = cfAt(mid);
    if (cf >= 0) low = mid;
    else high = mid;
  }
  return Math.round((low + high) / 2);
}

/** Benoetigter Mietfaktor k auf beide Mieten (W+G gleicher Multiplikator) fuer CF=0 --
 *  aus MixedUseCheck.tsx extrahiert (`breakEvenRentMultiplierForCashflowZeroMixed`),
 *  unveraendert uebernommen (siehe Kommentar oben). */
export function breakEvenRentMultiplierForCashflowZeroMixed(base: {
  financingOn: boolean;
  eigenkapital: number;
  nkPct: number;
  zinsPct: number;
  tilgungPct: number;
  wFl: number;
  wRentM2: number;
  wLeer: number;
  wOpexBrutto: number;
  gFl: number;
  gRentM2: number;
  gLeer: number;
  gOpexBrutto: number;
  kaufpreis: number;
  bewirtschaftungModus?: "pauschal" | "einzeln";
  wNichtUmlagefaehig?: number;
  wVerwaltung?: number;
  wInstandhaltungJeM2Jahr?: number;
  gNichtUmlagefaehig?: number;
  gVerwaltung?: number;
  gInstandhaltungJeM2Jahr?: number;
}): number {
  if (!base.financingOn || base.zinsPct + base.tilgungPct <= 0) return 1;
  // Kaufpreis bleibt hier fix (nur der Mietfaktor k variiert) -- Kredit einmal vorab ueber
  // computeFinancing() bestimmt (Dominiks Vorgabe, 16.10.2026, einheitliche Darlehensregel).
  const nebenkosten = Math.round(base.kaufpreis * base.nkPct);
  const loan = computeFinancing({ kaufpreis: base.kaufpreis, nebenkosten, eigenkapital: base.eigenkapital, financingOn: true }).loan;
  const annu = loan * (base.zinsPct + base.tilgungPct);
  // Bewirtschaftung Pauschal/Einzeln (Baustein 5) -- spiegelt den Modus-Zweig aus
  // computeMixedMetrics() oben, damit die Mindestmiete fuer einen Pauschal-/Einzeln-Deal
  // konsistent zur Objektseite bleibt. Pauschal/Legacy skalieren mit der (hier variierenden)
  // Miete, Einzeln ist ein fixer €-Betrag je Segment (nicht umlagefaehig + Verwaltung +
  // Instandhaltung -- Instandhaltung ist ohnehin flaechen-, nicht mietbasiert).
  const cfAt = (k: number) => {
    const grossW = base.wFl * (base.wRentM2 * k) * 12;
    const effW = grossW * (1 - base.wLeer);
    const grossG = base.gFl * (base.gRentM2 * k) * 12;
    const effG = grossG * (1 - base.gLeer);
    let opexW: number, opexG: number;
    if (base.bewirtschaftungModus === "pauschal") {
      opexW = effW * BEWIRTSCHAFTUNG_PAUSCHALE_PCT.mixeduse;
      opexG = effG * BEWIRTSCHAFTUNG_PAUSCHALE_PCT.mixeduse;
    } else if (base.bewirtschaftungModus === "einzeln") {
      opexW = Math.max(0, base.wNichtUmlagefaehig ?? 0) + Math.max(0, base.wVerwaltung ?? 0) + Math.max(0, base.wInstandhaltungJeM2Jahr ?? 0) * Math.max(0, base.wFl);
      opexG = Math.max(0, base.gNichtUmlagefaehig ?? 0) + Math.max(0, base.gVerwaltung ?? 0) + Math.max(0, base.gInstandhaltungJeM2Jahr ?? 0) * Math.max(0, base.gFl);
    } else {
      opexW = grossW * base.wOpexBrutto;
      opexG = grossG * base.gOpexBrutto;
    }
    const noi = effW - opexW + (effG - opexG);
    return noi - annu;
  };
  let low = 0.5, high = 2.5;
  for (let i = 0; i < 50; i++) {
    const mid = (low + high) / 2;
    const cf = cfAt(mid);
    if (cf >= 0) high = mid;
    else low = mid;
  }
  return (low + high) / 2;
}
